/**
 * AipPanelFrame — the dock's Assistant tab when this deployment uses
 * zehn-aip's doc agent panel (VITE_AIP_PANEL_URL).
 *
 * The dock page is the panel's host. It hands the panel the person's sign-in
 * (published by the editor page, utils/aipPanel.js) and the open file, and it
 * relays the panel's editor work to branding.js — which takes editor messages
 * only from this dock frame — answering with the editor's reply. Only the
 * messages below are relayed; every message is pinned to the panel's origin
 * and must come from the panel's own window.
 *
 * The panel's ready names what it draws itself (`capabilities`; an older
 * panel names none). One with 'chrome' has its own header, and shows its close
 * button when the context says `closable` — the dock then draws no bar of its
 * own; that button asks for the dock to collapse (`zehn-aip:close`, reason
 * 'button'). Escape would close only a modal host, and the dock never is one
 * (`modal: false`). Once ready, the panel is told whether its tab is shown
 * (`zehn-aip:visibility`), and again whenever that changes.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { originOf } from '@/shared/utils/collaboraOrigin.js';
import { aipPanelUrl, requestAipAuth, subscribeAipAuth } from '@/shared/utils/aipPanel';

// Editor work the panel may ask for, with how long the editor gets.
const RELAYED = {
  Zehn_GetSelection: 3000,
  Zehn_ApplyOps: 60000,
  Zehn_RequestSave: 12000,
  Zehn_DocOp: 30000,
  Zehn_FillBookmarks: 30000,
};

export function AipPanelFrame({
  file,
  editorBridge,
  onDocumentChanged,
  visible = true,
  closable = false,
  onClose,
  onCapabilities,
}) {
  const { i18n } = useTranslation();
  const frameRef = useRef(null);
  const panelReady = useRef(false);
  const url = aipPanelUrl();
  const panelOrigin = originOf(url);
  const src = useMemo(() => `${url.split('#')[0]}#host=${encodeURIComponent(window.location.origin)}`, [url]);

  const send = useCallback(
    (message) => frameRef.current?.contentWindow?.postMessage(message, panelOrigin),
    [panelOrigin],
  );
  const context = useCallback(
    () => ({
      ref: `docs:file:${file.id}`,
      file_id: Number(file.id) || file.id,
      file_name: file.title || '',
      can_edit: true,
      language: i18n.language,
      theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
      // The panel shows its own close button only when the dock draws none.
      closable,
      modal: false,
    }),
    [file.id, file.title, i18n.language, closable],
  );

  // What the listener needs from the latest render, mirrored into a ref: it is
  // bound once per panel origin, so a re-render never rebinds it, and what it
  // sends after an await (the init) carries the context as it is by then.
  const live = useRef({ context, visible, editorBridge, onDocumentChanged, onClose, onCapabilities });
  useEffect(() => {
    live.current = { context, visible, editorBridge, onDocumentChanged, onClose, onCapabilities };
  }, [context, visible, editorBridge, onDocumentChanged, onClose, onCapabilities]);

  useEffect(() => {
    // The panel always gets an init, with auth null when the editor page has
    // no sign-in to give, so it can say so instead of waiting. A sign-in the
    // editor page publishes later comes as that init, with the file; after
    // it, renewals go straight on.
    let signedIn = false;
    const init = (auth) => {
      signedIn = Boolean(auth);
      send({ type: 'zehn-aip:init', auth: auth || null, context: live.current.context() });
    };
    const offAuth = subscribeAipAuth((auth) => (signedIn ? send({ type: 'zehn-aip:auth', auth }) : init(auth)));

    const onMessage = async (event) => {
      if (event.source !== frameRef.current?.contentWindow || event.origin !== panelOrigin) return;
      const data = event.data || {};
      if (data.type === 'zehn-aip:ready') {
        panelReady.current = true;
        const capabilities = Array.isArray(data.capabilities)
          ? data.capabilities.filter((capability) => typeof capability === 'string')
          : [];
        live.current.onCapabilities?.(capabilities);
        send({ type: 'zehn-aip:visibility', visible: live.current.visible });
        init(await requestAipAuth());
      } else if (data.type === 'zehn-aip:close') {
        // Its close button; Escape is not the panel's to close a dock with.
        if (data.reason === 'button') live.current.onClose?.();
      } else if (data.type === 'zehn-aip:token-expired') {
        const auth = await requestAipAuth({ fresh: true });
        if (auth) send({ type: 'zehn-aip:auth', auth });
      } else if (data.type === 'zehn-aip:editor' && RELAYED[data.messageId]) {
        const bridge = live.current.editorBridge;
        if (!bridge?.request) return;
        const result = await bridge.request(data.messageId, data.values || {}, RELAYED[data.messageId]);
        send({ type: 'zehn-aip:editor-result', requestId: data.requestId, result });
        if (data.messageId === 'Zehn_RequestSave' && result?.ok) live.current.onDocumentChanged?.();
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      offAuth();
    };
  }, [panelOrigin, send]);

  // The panel is already up when the file, the language or `closable` change…
  useEffect(() => {
    if (panelReady.current) send({ type: 'zehn-aip:context', context: context() });
  }, [context, send]);

  // …and when its tab is shown or hidden.
  useEffect(() => {
    if (panelReady.current) send({ type: 'zehn-aip:visibility', visible });
  }, [visible, send]);

  return (
    <iframe
      ref={frameRef}
      src={src}
      title="Zehn assistant"
      className="min-h-0 w-full flex-1 border-0"
      allow="clipboard-write"
    />
  );
}
